import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'

// Components
import StickyHeaderLayoverModal from '../feedback/StickyHeaderLayoverModal'
import Text from '../Text'
import PeopleRow from '../feedback/PeopleRow'
import StatusTag from '../tag/StatusTag'
import Icon from '../icon/Icon'
import CircleIcon from '../icon/CircleIcon'
import { RowAligned } from '../layout/LayoutAtoms'
import AssessmentCard from './AssessmentCard'
import EvaluationModal from '../EvaluationModal'

// Styles
import { mqFrom } from '../../styles/mediaQueries'

// Utils
import { useAppState } from '../../context/AppContext'
import { formatDate } from '../../utils/dateHelper'
import { loadPersonalPdpService } from '../../context/services/pdp/loadPersonalPdp.service'

// Types
import { IconType } from '../icon/IconType'
import { ICompanyMember } from '../../common/models/Company'
import {
  IEvaluationPure,
  ISingleEvaluationPure,
  IEvaluationAssessor,
  SingleEvaluationStatus,
  EvaluationAssessmentType,
} from '../../common/models/Evaluation'
import { IPdpPure } from '../../common/models/Pdp'

interface IProps {
  evaluationName: string
  evaluations: IEvaluationPure[]
  closeModal: () => void
}

const EvaluateRequestModal: React.FC<IProps> = ({
  closeModal,
  evaluations,
  evaluationName,
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  const { user, company, theme, userPdp, userAuthToken } = useAppState()
  const [pdp, setPdp] = useState<IPdpPure>()
  const [selectedMember, setSelectedMember] = useState<{
    member: ICompanyMember
    assessment: ISingleEvaluationPure
    assessor: IEvaluationAssessor
    evaluation: IEvaluationPure
  }>()
  const [
    openEvaluationModal,
    setOpenEvaluationModal,
  ] = useState<EvaluationAssessmentType>()

  // Stable array reference so EvaluationModal doesn't rebuild its state (and
  // reset the active tab) on every unrelated re-render — e.g. the 30s
  // auth-token poll that re-renders all useAppState consumers.
  const modalEvaluations = useMemo(
    () => (selectedMember?.evaluation ? [selectedMember.evaluation] : []),
    [selectedMember]
  )

  const authMember = user?.asCompanyMember

  const selfEvaluation = evaluations.find((e) => e.memberId === authMember?._id)
  const selfAssessment = selfEvaluation?.assessment.find(
    (a) => a.senderId === authMember?._id
  )
  const selfAssessor = selfEvaluation?.assessors.find(
    (as) => as.memberId === authMember?._id
  )

  const getPdp = async (
    memberId: string,
    termNumber: number,
    evaluationId: string
  ) => {
    if (evaluations.length === 0 || !company || !userAuthToken) {
      return
    }

    if (authMember?._id === memberId && termNumber === userPdp?.termNumber) {
      setPdp(userPdp)
      return
    }

    const { savedPdp } = await loadPersonalPdpService(
      memberId,
      termNumber,
      userAuthToken,
      company,
      evaluationId
    )

    setPdp(savedPdp || pdp)
  }

  const openAssessments = (assessor: IEvaluationAssessor) =>
    ['Skills', 'Goals', 'Culture'].filter((e) => assessor[e.toLowerCase()])

  const getTags = (
    assessment: ISingleEvaluationPure,
    assessor: IEvaluationAssessor
  ) => {
    if (assessment.status === SingleEvaluationStatus.DONE) {
      return (
        <StyledTag
          caption={
            <>
              <Icon
                type="Check"
                color={theme.colors.primaryFadeSemi}
                size={10}
              />
              <Text tag="span" textStyle="caption" color="textLight">
                {t('general.states.done')}
              </Text>
            </>
          }
          color="textLightest"
        />
      )
    } else {
      return openAssessments(assessor).map((icon) => {
        const done =
          assessment[`${icon.toLowerCase()}Submitted`] &&
          assessment[icon.toLowerCase()]?.length > 0
        return (
          <StyledCircleIcon
            key={`circle-icon-${icon}`}
            type={icon as IconType}
            size={16}
            color={done ? theme.colors.textLighter : theme.colors.textLight}
          />
        )
      })
    }
  }

  return (
    <>
      <StickyHeaderLayoverModal
        closeModal={closeModal}
        headerConfig={{
          onBack: selectedMember
            ? () => setSelectedMember(undefined)
            : undefined,
          cta: selectedMember
            ? {
                member: selectedMember.member,
                text: t('evaluateRequestModal.requestedOn', {
                  date: formatDate(
                    new Date(selectedMember.assessment.dateSubmitted)
                  ),
                }),
              }
            : undefined,
          subtitle: selectedMember
            ? t('evaluateRequestModal.subtitle')
            : t('general.openRequest'),
          title: selectedMember
            ? undefined
            : t('requests.requestCompleteAssessments', {
                name: evaluationName,
              }),
        }}
      >
        {!selectedMember ? (
          <>
            {selfEvaluation && selfAssessment && authMember && selfAssessor && (
              <>
                <Text textStyle="title" marginBottom={12} color="textDark">
                  {t('evaluateRequestModal.evaluateYourself')}
                </Text>
                <StyledPeopleRow
                  type="outgoing"
                  name={authMember?.name || ''}
                  date={formatDate(new Date(selfAssessment?.dateSubmitted))}
                  avatars={[authMember, authMember]}
                  elements={getTags(selfAssessment, selfAssessor)}
                  cta={{
                    type: 'start',
                    onClick: () =>
                      setSelectedMember({
                        member: authMember,
                        assessment: selfAssessment,
                        assessor: selfAssessor,
                        evaluation: selfEvaluation,
                      }),
                  }}
                />
              </>
            )}
            {evaluations.some((e) => e.memberId !== authMember?._id) && (
              <>
                <Text
                  textStyle="title"
                  marginBottom={12}
                  marginTop={16}
                  color="textDark"
                >
                  {t('evaluateRequestModal.otherEvaluations')}
                </Text>
                {evaluations
                  .filter((e) => e.memberId !== user?.asCompanyMember?._id)
                  .map((evaluation) => {
                    const evaluateMember = company?.members.find(
                      (member) => evaluation.memberId === member._id
                    )
                    const assessment = evaluation.assessment.find(
                      (a) => a.senderId === authMember?._id
                    )

                    const assessor = evaluation.assessors.find(
                      (as) => as.memberId === authMember?._id
                    )

                    return assessment && assessor && evaluateMember ? (
                      <StyledPeopleRow
                        key={evaluation._id}
                        type="outgoing"
                        name={evaluateMember?.name || ''}
                        date={formatDate(new Date(assessment?.dateSubmitted))}
                        avatars={[authMember!, evaluateMember]}
                        elements={getTags(assessment, assessor)}
                        cta={{
                          type: 'start',
                          onClick: () =>
                            setSelectedMember({
                              member: evaluateMember,
                              assessment,
                              assessor,
                              evaluation,
                            }),
                        }}
                      />
                    ) : null
                  })}
              </>
            )}
          </>
        ) : (
          <>
            {selectedMember.assessment.status !==
              SingleEvaluationStatus.DONE && (
              <>
                <Text textStyle="title" marginBottom={16} color="textDark">
                  {t('evaluateRequestModal.openAssessments')}
                </Text>
                <RowAligned margin="0 0 24px">
                  {openAssessments(selectedMember.assessor)
                    .filter(
                      (as) =>
                        !selectedMember.assessment[
                          `${as.toLowerCase()}Submitted`
                        ]
                    )
                    .map((as) => (
                      <StyledAssessmentCard
                        key={`open-assessment-${as}`}
                        assessment={selectedMember.assessment}
                        type={as.toLowerCase() as EvaluationAssessmentType}
                        onClick={() => {
                          history.push(
                            `/evaluation/${
                              selectedMember.evaluation._id
                            }/${as.toLowerCase()}`
                          )
                        }}
                      />
                    ))}
                </RowAligned>
              </>
            )}
            {openAssessments(selectedMember.assessor).filter(
              (as) => selectedMember.assessment[`${as.toLowerCase()}Submitted`]
            ).length > 0 && (
              <>
                <Text textStyle="title" marginBottom={16} color="textDark">
                  {t('evaluateRequestModal.completedAssessments')}
                </Text>
                {openAssessments(selectedMember.assessor)
                  .filter(
                    (as) =>
                      selectedMember.assessment[`${as.toLowerCase()}Submitted`]
                  )
                  .map((asmnt) => (
                    <StyledPeopleRow
                      key={`done-assessment-${asmnt}`}
                      type="incoming"
                      name={`${asmnt} ${t('general.assessment')}`}
                      date={`${t('general.states.completed')} ${t(
                        'general.on'
                      ).toLowerCase()} ${formatDate(
                        new Date(
                          selectedMember.assessment[
                            `${asmnt.toLowerCase()}Submitted`
                          ]
                        )
                      )}`}
                      avatars={[asmnt as IconType]}
                      status="done"
                      cta={{
                        type: 'show',
                        onClick: async () => {
                          if (asmnt === 'Goals') {
                            await getPdp(
                              selectedMember.member._id,
                              selectedMember.evaluation.termNumber,
                              selectedMember.evaluation._id
                            )
                          }
                          setOpenEvaluationModal(
                            asmnt.toLowerCase() as EvaluationAssessmentType
                          )
                        },
                      }}
                    />
                  ))}
              </>
            )}
          </>
        )}
      </StickyHeaderLayoverModal>
      {/* The evaluation opens as a layer stacked on top of the request list;
          the stack's auto-back returns here, X/dim closes the whole stack. */}
      {openEvaluationModal && selectedMember && (
        <EvaluationModal
          pdp={pdp!}
          closeModal={() => setOpenEvaluationModal(undefined)}
          defaultFilter={openEvaluationModal}
          evaluations={modalEvaluations}
          initialType={
            openEvaluationModal === 'culture'
              ? 'culturesResults'
              : openEvaluationModal === 'skills'
              ? 'skillResults'
              : 'goalsResults'
          }
          member={selectedMember.member}
          isOwnProfile={selectedMember.member._id === authMember?._id}
        />
      )}
    </>
  )
}

export default EvaluateRequestModal

const StyledPeopleRow = styled(PeopleRow)`
  margin-bottom: 8px;
`

const StyledTag = styled(StatusTag)`
  text-transform: initial;
  display: flex;
  align-items: center;
  margin-right: 16px;
`

const StyledCircleIcon = styled(CircleIcon)`
  margin-left: 8px;

  &:last-of-type {
    margin-right: 16px;
  }
`

const StyledAssessmentCard = styled(AssessmentCard)`
  ${mqFrom.M`
    width: calc(33.33% - 10px);
    margin-right: 16px;
    flex: none;

    &:last-of-type {
      margin-right: 0;
    }
  `}
`
